import type {OrderCsvImportProgressItem, OrderImportProgressItem} from './orderStore.types';
import type {NoteFromPrincipal, Order} from '@myparcel-frontend/order-service';
import {activeOrderStatuses, OrderStatus} from '@myparcel-frontend/order-service';
import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {createSnapshotStore} from '@/store/snapshotStore/snapshotStore';
import {TemporaryOrderNoteIdPrefix} from '@/types/order';

const useBulkImportSnapshotStore = createSnapshotStore<OrderImportProgressItem>('orderBulkImport');
const useBulkCsvImportSnapshotStore = createSnapshotStore<OrderCsvImportProgressItem>('orderBulkCsvImport');

export const useOrderStore = defineStore('orders', () => {
  const bulkImportSnapshot = useBulkImportSnapshotStore();
  const bulkCsvImportSnapshot = useBulkCsvImportSnapshotStore();

  const orderList = ref<Order[]>([]);

  // Disables order-import action buttons everywhere while a bulk import request is running
  const isBulkImporting = ref(false);

  // Statuses that received an order via a websocket event which the backend's last aggregation count doesn't reflect yet
  const statusesWithNewOrders = ref<Partial<Record<OrderStatus, boolean>>>({});

  const markStatusHasNewOrders = (status: OrderStatus) => {
    statusesWithNewOrders.value[status] = true;
  };

  const clearNewOrderStatus = (status: OrderStatus) => {
    delete statusesWithNewOrders.value[status];
  };

  const resetNewOrderStatuses = () => {
    statusesWithNewOrders.value = {};
  };

  const flagIncomingOrder = (order: Pick<Order, 'id' | 'status'>) => {
    if (!activeOrderStatuses.includes(order.status)) {
      return;
    }

    const previous = getOrderById(order.id);

    // A brand-new order, or an order that moved into this status, means the status now holds one
    // more order than the backend's last aggregation counted. A same-status field update doesn't.
    if (!previous || previous.status !== order.status) {
      markStatusHasNewOrders(order.status);
    }
  };

  const getOrderById = (id: string): Order | undefined => {
    return orderList.value.find((order: Order) => order.id === id);
  };

  const updateOrderStatus = (orderId: string, status: Order['status']) => {
    const order = getOrderById(orderId);

    if (order) {
      order.status = status;
    }
  };

  const assignedUserToOrder = ({orderId, userId}: {orderId: string; userId: string}) => {
    const order = getOrderById(orderId);

    if (!order) {
      throw new Error(`No order found with order.id: '${orderId}'`);
    }

    order.assignedUserId = userId;
  };

  const removeAssignedUserFromOrder = (orderId: string) => {
    const order = getOrderById(orderId);

    if (!order) {
      throw new Error(`No order found with order.id: '${orderId}'`);
    }

    order.assignedUserId = undefined;
  };

  const addOrderNote = ({orderId, newOrderNote}: {orderId: string; newOrderNote: NoteFromPrincipal}) => {
    const order = getOrderById(orderId);

    if (!order) {
      return;
    }

    // Websocket can respond earlier than the `/add-note` response, in that case we don't want to add the same note twice to the order
    // Websocket overrides the entire order object, so we only need to check this function for duplicates.
    const containsNote = order.notes?.find((note) => TemporaryOrderNoteIdPrefix + note.id === newOrderNote.id);

    if (containsNote) {
      return;
    }

    order.notes = order.notes ? [...order.notes, newOrderNote] : [newOrderNote];
  };

  const updateOrderNote = ({
    orderId,
    noteId,
    text,
    updatedAt,
  }: {
    orderId: string;
    noteId: string;
    text: string;
    updatedAt: Date;
  }) => {
    const order = getOrderById(orderId);

    if (!order) {
      return;
    }

    if (!order.notes) {
      return;
    }

    const index = order.notes.findIndex((note) => note.id === noteId);

    if (index === -1) {
      return;
    }

    order.notes[index].text = text;
    order.notes[index].updatedAt = updatedAt.toISOString();

    return order.notes[index];
  };

  const removeOrderNotes = ({orderId, noteIds}: {orderId: string; noteIds: string[]}) => {
    const order = getOrderById(orderId);

    if (!order) {
      return;
    }

    if (!order.notes) {
      return;
    }

    order.notes = order.notes.filter((note) => !noteIds.includes(note.id));

    if (order.notes.length === 0) {
      delete order.notes;
    }
  };

  const removeOrder = (orderId: string) => {
    orderList.value = orderList.value.filter((order) => order.id !== orderId);
  };

  const orderListAmount = computed(
    () => orderList.value.filter((order) => order.status !== OrderStatus.DISCOVERED).length,
  );

  return {
    addOrderNote,
    assignedUserToOrder,
    getOrderById,
    removeOrder,
    orderList,
    orderListAmount,
    bulkImportSnapshot,
    bulkCsvImportSnapshot,
    isBulkImporting,
    removeAssignedUserFromOrder,
    removeOrderNotes,
    updateOrderStatus,
    updateOrderNote,
    statusesWithNewOrders,
    markStatusHasNewOrders,
    clearNewOrderStatus,
    resetNewOrderStatuses,
    flagIncomingOrder,
  };
});
